/**
 * TweenMovementComponent animates an actor's transform using Tween.js.
 *
 * This component provides an editor-friendly way to create animated movement.
 * It uses the world's TweenManager for all tween updates.
 *
 * For code-based tweening, use world.tweenManager directly:
 * ```ts
 * const tween = world.tweenManager.createTween(object)
 *   .to({ x: 100 }, 1000)
 *   .easing(TWEEN.Easing.Cubic.InOut)
 *   .start();
 * ```
 */
import * as TWEEN from '@tweenjs/tween.js';
import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';
import { SceneComponent } from '../SceneComponent.js';

import type { SceneComponentOptions } from '../SceneComponent.js';

export type TweenEasingType =
  | 'Linear'
  | 'Quadratic.In' | 'Quadratic.Out' | 'Quadratic.InOut'
  | 'Cubic.In' | 'Cubic.Out' | 'Cubic.InOut'
  | 'Quartic.In' | 'Quartic.Out' | 'Quartic.InOut'
  | 'Quintic.In' | 'Quintic.Out' | 'Quintic.InOut'
  | 'Sinusoidal.In' | 'Sinusoidal.Out' | 'Sinusoidal.InOut'
  | 'Exponential.In' | 'Exponential.Out' | 'Exponential.InOut'
  | 'Circular.In' | 'Circular.Out' | 'Circular.InOut'
  | 'Elastic.In' | 'Elastic.Out' | 'Elastic.InOut'
  | 'Back.In' | 'Back.Out' | 'Back.InOut'
  | 'Bounce.In' | 'Bounce.Out' | 'Bounce.InOut';

export type TweenLoopMode = 'NoLoop' | 'Loop';

export interface TweenMovementComponentOptions extends SceneComponentOptions {
  /** Duration in seconds */
  duration?: number;
  /** Target position for animation */
  targetPosition?: THREE.Vector3;
  /** Target rotation for animation (in degrees) */
  targetRotation?: THREE.Vector3;
  /** Target scale for animation */
  targetScale?: THREE.Vector3;
  /** Easing function */
  easing?: TweenEasingType;
  /** Loop mode */
  loop?: TweenLoopMode;
  /** Whether to start automatically on begin play */
  autoStart?: boolean;
}

@EngineClass('TweenMovementComponent')
export class TweenMovementComponent extends SceneComponent {


  @property({ type: 'number', min: 0, step: 0.1 })
  public duration: number = 1;

  @property()
  public targetPosition: THREE.Vector3 = new THREE.Vector3(0, 0, 0);

  @property()
  public targetRotation: THREE.Vector3 = new THREE.Vector3(0, 0, 0);

  @property()
  public targetScale: THREE.Vector3 = new THREE.Vector3(1, 1, 1);

  @property()
  public easing: TweenEasingType = 'Cubic.InOut';

  @property()
  public loop: TweenLoopMode = 'NoLoop';

  @property()
  public autoStart: boolean = false;

  private currentTween: TWEEN.Tween<any> | null = null;
  private rootComponent: SceneComponent | null = null;
  private startPosition: THREE.Vector3 = new THREE.Vector3();
  private startRotation: THREE.Euler = new THREE.Euler();
  private startScale: THREE.Vector3 = new THREE.Vector3(1, 1, 1);
  private isPlaying: boolean = false;

  // Editor visualization
  private debugLine: THREE.Line | null = null;
  private debugSpheres: THREE.Mesh[] = [];
  private debugGroup: THREE.Group | null = null;
  private debugArrow: THREE.ArrowHelper | null = null;

  public override initialize(options?: TweenMovementComponentOptions): void {
    super.initialize(options);
    this.duration = options?.duration ?? this.duration;
    this.targetPosition = options?.targetPosition ?? this.targetPosition;
    this.targetRotation = options?.targetRotation ?? this.targetRotation;
    this.targetScale = options?.targetScale ?? this.targetScale;
    this.easing = options?.easing ?? this.easing;
    this.loop = options?.loop ?? this.loop;
    this.autoStart = options?.autoStart ?? this.autoStart;
  }

  public override beginPlay(): void {
    super.beginPlay();

    const actor = this.getActor();
    if (!actor) {
      console.warn('TweenMovementComponent: No actor attached');
      return;
    }

    this.rootComponent = actor.rootComponent;
    if (!this.rootComponent) {
      console.warn('TweenMovementComponent: Actor has no root scene component');
      return;
    }

    // Store initial transform
    this.rootComponent.getWorldPosition(this.startPosition);
    this.rootComponent.getWorldRotation(this.startRotation);
    this.rootComponent.getWorldScale(this.startScale);

    if (this.autoStart) {
      this.startTween();
    }
  }

  public override endPlay(): void {
    this.stopTween();
    this.cleanupEditorVisualization();
    super.endPlay();
  }

  public startTween(): void {
    const world = this.getWorld();
    if (!world || !this.rootComponent) {
      console.warn('TweenMovementComponent: Cannot start tween');
      return;
    }

    this.stopTween();

    const duration = this.duration * 1000;
    const easingFn = this.getEasingFunction(this.easing);

    // Build target transform
    const targetPos = this.targetPosition;
    const targetRot = this.targetRotation;
    const targetScale = this.targetScale;

    // Create object to tween
    const tweenData = {
      px: this.startPosition.x,
      py: this.startPosition.y,
      pz: this.startPosition.z,
      rx: this.startRotation.x,
      ry: this.startRotation.y,
      rz: this.startRotation.z,
      sx: this.startScale.x,
      sy: this.startScale.y,
      sz: this.startScale.z,
    };

    const target = {
      px: targetPos.x,
      py: targetPos.y,
      pz: targetPos.z,
      rx: THREE.MathUtils.degToRad(targetRot.x),
      ry: THREE.MathUtils.degToRad(targetRot.y),
      rz: THREE.MathUtils.degToRad(targetRot.z),
      sx: targetScale.x,
      sy: targetScale.y,
      sz: targetScale.z,
    };

    // Create tween using world's TweenManager
    this.currentTween = world.tweenManager.createTween(tweenData)
      .to(target, duration)
      .easing(easingFn)
      .onUpdate(() => {
        if (!this.rootComponent) return;

        this.rootComponent.setWorldPosition(new THREE.Vector3(
          tweenData.px,
          tweenData.py,
          tweenData.pz
        ));
        this.rootComponent.setWorldRotation(new THREE.Euler(
          tweenData.rx,
          tweenData.ry,
          tweenData.rz
        ));
        this.rootComponent.setWorldScale(new THREE.Vector3(
          tweenData.sx,
          tweenData.sy,
          tweenData.sz
        ));
      })
      .onComplete(() => {
        if (this.loop === 'Loop') {
          // Reset and restart
          this.resetToStart();
          this.startTween();
        } else {
          this.isPlaying = false;
        }
      })
      .start(world.tweenManager.getCurrentTime());

    this.isPlaying = true;
  }

  public stopTween(): void {
    if (this.currentTween) {
      const world = this.getWorld();
      if (world) {
        world.tweenManager.stopTween(this.currentTween);
      }
      this.currentTween = null;
    }
    this.isPlaying = false;
  }

  public resetToStart(): void {
    if (!this.rootComponent) return;

    this.rootComponent.setWorldPosition(this.startPosition.clone());
    this.rootComponent.setWorldRotation(this.startRotation.clone());
    this.rootComponent.setWorldScale(this.startScale.clone());
  }

  private getEasingFunction(easing?: TweenEasingType): (t: number) => number {
    if (!easing) return TWEEN.Easing.Linear.None;

    const parts = easing.split('.');
    if (parts.length === 1) {
      return TWEEN.Easing.Linear.None;
    }

    const category = parts[0] as keyof typeof TWEEN.Easing;
    const type = parts[1] as 'In' | 'Out' | 'InOut' | 'None';

    if (category in TWEEN.Easing && type in TWEEN.Easing[category]) {
      return (TWEEN.Easing[category] as any)[type];
    }

    return TWEEN.Easing.Linear.None;
  }

  //====================================================================================================
  // Editor Visualization
  //====================================================================================================

  private initializeEditorVisualization(): void {
    const world = this.getWorld();
    if (!world || !this.rootComponent) return;

    this.debugGroup = new THREE.Group();
    this.debugGroup.name = `TweenDebug_${this.getActor()?.uuid ?? 'unknown'}`;
    world.scene.add(this.debugGroup);

    this.updateEditorVisualization();

    this.debugGroup.traverse((child) => {
      child.raycast = () => { };
    });
  }

  private updateEditorVisualization(): void {
    if (!this.debugGroup || !this.rootComponent) return;

    if (this.debugLine) {
      this.debugLine.geometry.dispose();
      (this.debugLine.material as THREE.Material).dispose();
      this.debugGroup.remove(this.debugLine);
      this.debugLine = null;
    }

    this.debugSpheres.forEach(sphere => {
      sphere.geometry.dispose();
      (sphere.material as THREE.Material).dispose();
      this.debugGroup!.remove(sphere);
    });
    this.debugSpheres = [];

    this.disposeDebugArrow();

    const targetPos = this.targetPosition;

    const points = [
      this.startPosition.clone(),
      new THREE.Vector3(targetPos.x, targetPos.y, targetPos.z)
    ];

    // Line
    const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0x00ffff,
      linewidth: 2,
      transparent: true,
      opacity: 0.7
    });
    this.debugLine = new THREE.Line(lineGeometry, lineMaterial);
    this.debugGroup.add(this.debugLine);

    // Spheres
    points.forEach((point, index) => {
      const sphereGeometry = new THREE.SphereGeometry(0.15, 8, 6);
      const sphereMaterial = new THREE.MeshBasicMaterial({
        color: index === 0 ? 0x00ff00 : 0xff0000,
        transparent: true,
        opacity: 0.8
      });
      const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
      sphere.position.copy(point);

      this.debugGroup!.add(sphere);
      this.debugSpheres.push(sphere);
    });

    // Arrow. ArrowHelper's `line` and `cone` ship with module-level *shared*
    // geometries from three.core (`_lineGeometry` / `_coneGeometry`). We
    // clone them so we own per-instance copies that can be safely disposed
    // — otherwise the renderer's `dispose` listener registered on the
    // shared geometry would keep this Line/Mesh and the entire debug graph
    // alive forever (one accumulated leak per visualization rebuild).
    const direction = new THREE.Vector3().subVectors(points[1], points[0]).normalize();
    const arrowPos = new THREE.Vector3().lerpVectors(points[0], points[1], 0.75);
    const arrowHelper = new THREE.ArrowHelper(direction, arrowPos, 0.5, 0x00ffff, 0.2, 0.15);
    arrowHelper.line.geometry = arrowHelper.line.geometry.clone();
    arrowHelper.cone.geometry = arrowHelper.cone.geometry.clone();
    this.debugGroup!.add(arrowHelper);
    this.debugArrow = arrowHelper;
  }

  private disposeDebugArrow(): void {
    if (!this.debugArrow) return;
    this.debugArrow.removeFromParent();
    // `ArrowHelper.dispose()` internally calls dispose on `line.geometry`,
    // `line.material`, `cone.geometry` and `cone.material`. Because
    // `updateEditorVisualization` replaced the shared singleton geometries
    // with per-instance clones, this single call is now safe and complete.
    this.debugArrow.dispose();
    this.debugArrow = null;
  }

  private cleanupEditorVisualization(): void {
    if (this.debugGroup) {
      this.debugGroup.removeFromParent();

      if (this.debugLine) {
        this.debugLine.geometry.dispose();
        (this.debugLine.material as THREE.Material).dispose();
      }

      this.debugSpheres.forEach(sphere => {
        sphere.geometry.dispose();
        (sphere.material as THREE.Material).dispose();
      });

      this.disposeDebugArrow();

      this.debugGroup = null;
      this.debugLine = null;
      this.debugSpheres = [];
    }
  }

  public override onEditorSelectedChanged(isSelected: boolean): void {
    if (isSelected) {
      // Create visualization if it doesn't exist
      if (!this.debugGroup) {
        this.initializeEditorVisualization();
      }
    } else {
      // Clean up when deselected
      this.cleanupEditorVisualization();
    }
  }

  public override onEditorRemoveFromWorld(): void {
    this.cleanupEditorVisualization();
    super.onEditorRemoveFromWorld();
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_Path';
  }
}
